/**
 * Helper functions for formatting match times and dates
 * Extracted from app/friends.tsx
 */

import { isSameDay, formatShortDate, formatTime24h } from '../../../helpers/dateFormatters.helper'

/**
 * Formats a match date/time into a readable string
 * @param dateInput - Date string or Date object
 * @returns Formatted string like "Today 15:30" or "Dec 25 20:00"
 */
export const formatMatchTime = (dateInput: string | Date, timeZone?: string) => {
  const date = new Date(dateInput)
  const now = new Date()
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)

  const dateLabel = isSameDay(date, now)
    ? 'Today'
    : isSameDay(date, tomorrow)
    ? 'Tomorrow'
    : formatShortDate(date)

  const timeLabel = formatTime24h(date, timeZone ?? undefined)

  return `${dateLabel} ${timeLabel}`
}

/**
 * Calculates the time until a match starts or elapsed time if live
 * @param dateInput - Date string or Date object
 * @param matchStatus - Optional match status ('FINISHED', 'LIVE', 'SCHEDULED')
 * @returns Formatted string like "2h 30m", "45'", "Finished", etc.
 */
export const calculateTimeToStart = (
  dateInput: string | Date,
  matchStatus?: string
) => {
  const date = new Date(dateInput)
  const now = new Date()

  if (matchStatus === 'FINISHED') {
    return 'Finished'
  }

  if (matchStatus === 'LIVE') {
    const elapsedMs = now.getTime() - date.getTime()
    const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60))
    if (elapsedMinutes >= 150) {
      return '90+'
    }
    return `${elapsedMinutes}'`
  }

  const diffMs = date.getTime() - now.getTime()
  if (diffMs <= 0) {
    return 'Starting soon'
  }

  const diffMinutesTotal = Math.floor(diffMs / (1000 * 60))
  if (diffMinutesTotal >= 60) {
    const hours = Math.floor(diffMinutesTotal / 60)
    const minutes = diffMinutesTotal % 60
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`
  }

  return `${diffMinutesTotal}m`
}

export const getEntityName = (
  entity: { name?: string } | string | undefined,
  fallback: string
) => {
  if (!entity) return fallback
  if (typeof entity === 'string') return entity
  return entity.name || fallback
}


